"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import DOMPurify from "isomorphic-dompurify";
import { forwardRef, useReducer, useRef, useImperativeHandle, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { Plus, Trash, Minus } from "@nxt-costco-com/forge-icons";
import { Button } from "../Button/index.js";
import { IconButton } from "../IconButton/index.js";
import { InputError } from "../InputError/index.js";
import { DEFAULT_INCREMENT_ARIA_LABEL, DEFAULT_DECREMENT_ARIA_LABEL, DEFAULT_INPUT_ARIA_LABEL, DEFAULT_REMOVE_IN_CART_LABEL, ADD_TO_CART_LABEL, QUANTITY_LABEL, IN_CART_LABEL, DEFAULT_ERROR_STATE, DEFAULT_MIN_ERROR_MSG, DEFAULT_MAX_ERROR_MSG, ACTION_TYPES, COMPONENT_NAME } from "./constants.js";
import { getLiveRegionText } from "./lib.js";
import { quantitySelectorReducer } from "./reducer.js";
import { addToCartBtnStyles, buttonGroupStyles, iconButtonStyles, iconStyleRemove, defaultIconStyle, StyledInput } from "./styles.js";
import { QuantitySelectorSkeleton } from "./view.skeleton.js";
const QuantitySelector = forwardRef((props, outerRef) => {
  const {
    updateQuantity,
    onQuantityChange,
    customErrorMsg,
    incrementAriaLabel = DEFAULT_INCREMENT_ARIA_LABEL,
    decrementAriaLabel = DEFAULT_DECREMENT_ARIA_LABEL,
    inputAriaLabel = DEFAULT_INPUT_ARIA_LABEL,
    uniqueId = "",
    minValue = 1,
    maxValue = 999,
    isAddToCartVariant = false,
    isAddToCartVisible = false,
    isLoading = false,
    parentUniqueId,
    removeCartAriaLabel = DEFAULT_REMOVE_IN_CART_LABEL,
    maxLength = maxValue.toString().length || 3,
    addToCartLabel = ADD_TO_CART_LABEL,
    quantityLabel = QUANTITY_LABEL,
    inCartLabel = IN_CART_LABEL,
    maxErrorMsg,
    minErrorMsg,
    action = "",
    sx,
    allowAddToCartDisplay = true,
    showAddToCartPlusIcon = false,
    isDeleteFromCartMessage = true
  } = props;
  const [quantitySelectorState, dispatch] = useReducer(
    quantitySelectorReducer,
    {
      minValue,
      maxValue,
      quantityInput: `${minValue}`,
      showAddToCart: isAddToCartVisible,
      errorState: DEFAULT_ERROR_STATE,
      addToCartBtnFocus: false,
      liveRegionText: ""
    }
  );
  const inputRef = useRef(null);
  const addToCartButtonRef = useRef(null);
  const incrementIconButtonRef = useRef(null);
  const decrementIconButtonRef = useRef(null);
  const {
    prevQty,
    quantityInput,
    showAddToCart,
    errorState,
    addToCartBtnFocus,
    liveRegionText
  } = quantitySelectorState;
  const { errorStatus, errorMsg } = errorState;
  const defaultMinErrorMsg = customErrorMsg || (minErrorMsg || DEFAULT_MIN_ERROR_MSG).replace(
    "{minValue}",
    `${minValue}`
  );
  const defaultMaxErrorMsg = customErrorMsg || (maxErrorMsg || DEFAULT_MAX_ERROR_MSG).replace(
    "{maxValue}",
    `${maxValue}`
  );
  useImperativeHandle(outerRef, () => inputRef.current, []);
  const setInputFocus = useCallback(() => {
    var _a;
    if (isLoading) return;
    if ((_a = inputRef == null ? void 0 : inputRef.current) == null ? void 0 : _a.children[0]) {
      const inputElement = inputRef.current.children[0];
      inputElement.select();
      inputElement.focus();
    }
  }, [isLoading]);
  const validateQuantity = useCallback(
    /* istanbul ignore next */
    (qty) => {
      if (+qty > maxValue) {
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_VALUE,
          payload: {
            newQuantity: isAddToCartVariant && +qty > 0 ? qty : `${maxValue}`,
            liveRegionText: getLiveRegionText(
              maxValue,
              isAddToCartVariant,
              quantityLabel,
              inCartLabel
            ),
            errorObj: {
              errorStatus: true,
              errorMsg: defaultMaxErrorMsg
            }
          }
        });
      } else if (+qty < minValue && +qty !== 0 && !allowAddToCartDisplay) {
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_VALUE,
          payload: {
            showAddToCart: false,
            newQuantity: `${updateQuantity || minValue}`,
            liveRegionText: getLiveRegionText(
              isAddToCartVariant && +qty > 0 ? +qty : minValue,
              isAddToCartVariant,
              quantityLabel,
              inCartLabel
            ),
            errorObj: {
              errorStatus: (customErrorMsg || "").length > 0,
              errorMsg: customErrorMsg || ""
            }
          }
        });
      } else if (qty !== "" && !allowAddToCartDisplay) {
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_VALUE,
          payload: {
            showAddToCart: false,
            newQuantity: `${+qty > 0 ? qty : minValue}`,
            liveRegionText: getLiveRegionText(
              +qty,
              isAddToCartVariant,
              quantityLabel,
              inCartLabel
            ),
            errorObj: {
              errorStatus: (customErrorMsg || "").length > 0,
              errorMsg: customErrorMsg || ""
            }
          }
        });
      } else if (+qty < minValue && +qty !== 0) {
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_VALUE,
          payload: {
            showAddToCart: isAddToCartVariant && isAddToCartVisible,
            newQuantity: isAddToCartVariant && +qty > 0 ? qty : `${minValue}`,
            addToCartBtnFocus: isAddToCartVariant && isAddToCartVisible,
            liveRegionText: getLiveRegionText(
              isAddToCartVariant && +qty > 0 ? +qty : minValue,
              isAddToCartVariant,
              quantityLabel,
              inCartLabel
            ),
            errorObj: {
              errorStatus: true,
              errorMsg: defaultMinErrorMsg
            }
          }
        });
      } else if (qty !== "") {
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_VALUE,
          payload: {
            showAddToCart: qty === "0",
            newQuantity: `${qty}`,
            liveRegionText: getLiveRegionText(
              +qty,
              isAddToCartVariant,
              quantityLabel,
              inCartLabel
            ),
            errorObj: {
              errorStatus: (customErrorMsg || "").length > 0,
              errorMsg: customErrorMsg || ""
            }
          }
        });
      }
    },
    [
      customErrorMsg,
      defaultMaxErrorMsg,
      defaultMinErrorMsg,
      inCartLabel,
      isAddToCartVariant,
      isAddToCartVisible,
      maxValue,
      minValue,
      quantityLabel,
      allowAddToCartDisplay,
      updateQuantity
    ]
  );
  useEffect(() => {
    if (customErrorMsg) {
      dispatch({
        payload: {
          errorObj: {
            errorStatus: true,
            errorMsg: customErrorMsg
          }
        },
        type: ACTION_TYPES.SET_CUSTOM_ERROR
      });
    }
  }, [customErrorMsg]);
  useEffect(() => {
    if (updateQuantity) {
      validateQuantity(`${updateQuantity}`);
    }
  }, [updateQuantity, validateQuantity]);
  useEffect(() => {
    if (errorStatus && !isAddToCartVisible) {
      setInputFocus();
    }
  }, [errorStatus, isAddToCartVisible, setInputFocus]);
  useEffect(() => {
    var _a;
    if (addToCartBtnFocus && isAddToCartVariant) {
      (_a = addToCartButtonRef.current) == null ? void 0 : _a.focus();
    }
  }, [isAddToCartVariant, addToCartBtnFocus]);
  useEffect(() => {
    var _a, _b, _c;
    if (isLoading) return;
    if (action === ACTION_TYPES.ADD && !errorStatus) {
      (_a = decrementIconButtonRef.current) == null ? void 0 : _a.focus();
    } else if (action === ACTION_TYPES.INCREMENT && !errorStatus) {
      (_b = incrementIconButtonRef.current) == null ? void 0 : _b.focus();
    } else if (action === ACTION_TYPES.DECREMENT && !errorStatus) {
      (_c = decrementIconButtonRef.current) == null ? void 0 : _c.focus();
    } else if (action === ACTION_TYPES.SUBMIT) {
      setInputFocus();
    }
  }, [isLoading, action, setInputFocus, errorStatus]);
  const errorTextId = `quantitySelector_error_${uniqueId}`;
  const isTrashIcon = isAddToCartVariant && quantityInput !== "" && +quantityInput <= minValue;
  const LeftIcon = isTrashIcon ? Trash : Minus;
  const leftIconStyles = isTrashIcon ? { ...defaultIconStyle, ...iconStyleRemove } : defaultIconStyle;
  const updatedMinusAriaLabel = isTrashIcon ? removeCartAriaLabel : decrementAriaLabel;
  const dataTestId = `${COMPONENT_NAME}-${uniqueId}`;
  const onClickAddToCart = () => {
    dispatch({
      type: ACTION_TYPES.SET_CURRENT_VALUE,
      payload: {
        showAddToCart: false,
        newQuantity: `${minValue}`,
        addToCartBtnFocus: false,
        liveRegionText: getLiveRegionText(
          minValue,
          isAddToCartVariant,
          quantityLabel,
          inCartLabel
        )
      }
    });
    if (onQuantityChange) {
      onQuantityChange(minValue, ACTION_TYPES.ADD);
    }
  };
  const handleIncrement = () => {
    const incrementQty = quantityInput === "" ? prevQty || "" : quantityInput;
    validateQuantity(`${+incrementQty + 1}`);
    if (onQuantityChange) {
      onQuantityChange(+incrementQty + 1, ACTION_TYPES.INCREMENT);
    }
  };
  const handleDecrement = () => {
    const decrementQty = quantityInput === "" ? prevQty || "" : quantityInput;
    let updatedQuantity = +decrementQty - 1;
    if (updatedQuantity < 0) {
      updatedQuantity = 0;
    }
    validateQuantity(`${updatedQuantity}`);
    if (onQuantityChange) {
      onQuantityChange(updatedQuantity, ACTION_TYPES.DECREMENT);
    }
  };
  const handleChange = (e) => {
    const newQuantity = e.target.value.replace(/[+-]/g, "").trim();
    if (newQuantity === "" || !isNaN(+newQuantity)) {
      dispatch({
        type: ACTION_TYPES.CHANGE,
        payload: {
          newQuantity,
          liveRegionText: getLiveRegionText(
            +newQuantity,
            false,
            quantityLabel,
            inCartLabel
          )
        }
      });
      if (onQuantityChange) {
        onQuantityChange(+newQuantity, ACTION_TYPES.CHANGE);
      }
    }
  };
  const handleOnKeyDown = (e) => {
    if (e.key === "Enter") {
      validateQuantity(quantityInput);
      if (onQuantityChange) {
        onQuantityChange(
          parseInt(quantityInput) || 0,
          ACTION_TYPES.SUBMIT
        );
      }
    }
  };
  const handleOnBlur = (e) => {
    var _a, _b;
    if (((_a = e.relatedTarget) == null ? void 0 : _a.className.includes("buttonLeft")) || ((_b = e.relatedTarget) == null ? void 0 : _b.className.includes("buttonRight"))) {
      return;
    }
    validateQuantity(quantityInput === "" ? prevQty || "" : quantityInput);
    if (onQuantityChange) {
      onQuantityChange(
        +(quantityInput === "" ? prevQty || "" : quantityInput),
        ACTION_TYPES.BLUR
      );
    }
  };
  const sanitizedErrorMessage = DOMPurify.sanitize(errorMsg);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showAddToCart ? /* @__PURE__ */ jsx(
      Button,
      {
        variant: "primary",
        ref: addToCartButtonRef,
        sx: [
          addToCartBtnStyles,
          ...Array.isArray(sx) ? sx : [sx]
        ],
        onClick: onClickAddToCart,
        "data-testid": `addToCartBtn-${uniqueId}`,
        "aria-describedby": parentUniqueId,
        isLoading,
        startIcon: showAddToCartPlusIcon ? /* @__PURE__ */ jsx(
          Plus,
          {
            height: 16,
            width: 16,
            className: "addToCartPlusIcon"
          }
        ) : /* @__PURE__ */ jsx(Fragment, {}),
        children: addToCartLabel
      }
    ) : isLoading ? /* @__PURE__ */ jsx(QuantitySelectorSkeleton, { uniqueId }) : /* @__PURE__ */ jsxs(
      Box,
      {
        "data-testid": dataTestId,
        sx: [buttonGroupStyles, ...Array.isArray(sx) ? sx : [sx]],
        children: [
          /* @__PURE__ */ jsx(
            IconButton,
            {
              "data-testid": `qty-decrease-${uniqueId}`,
              "aria-label": updatedMinusAriaLabel,
              "aria-describedby": parentUniqueId,
              onClick: handleDecrement,
              sx: iconButtonStyles,
              className: "buttonLeft",
              ref: decrementIconButtonRef,
              children: /* @__PURE__ */ jsx(LeftIcon, { sx: leftIconStyles })
            }
          ),
          /* @__PURE__ */ jsx(
            StyledInput,
            {
              "data-testid": `qty-input-${uniqueId}`,
              disableUnderline: true,
              type: "tel",
              name: `inputName_${uniqueId}`,
              readOnly: false,
              inputProps: {
                "aria-label": inputAriaLabel,
                "aria-describedby": errorStatus ? errorTextId : parentUniqueId,
                "aria-required": true,
                maxLength,
                "aria-invalid": errorStatus
              },
              ref: inputRef,
              value: quantityInput,
              error: errorStatus,
              onChange: handleChange,
              onKeyDown: handleOnKeyDown,
              onBlur: handleOnBlur
            }
          ),
          /* @__PURE__ */ jsx(
            IconButton,
            {
              "data-testid": `qty-increase-${uniqueId}`,
              "aria-label": incrementAriaLabel,
              "aria-describedby": parentUniqueId,
              onClick: handleIncrement,
              sx: iconButtonStyles,
              className: "buttonRight",
              ref: incrementIconButtonRef,
              children: /* @__PURE__ */ jsx(Plus, { sx: defaultIconStyle })
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      InputError,
      {
        hasError: !isLoading && errorStatus && isDeleteFromCartMessage,
        errorTextId,
        errorText: sanitizedErrorMessage,
        errorIconA11yAttributes: {
          "aria-label": "Error",
          // TODO: we need a translated aria-label for this icon, see User Story 180526
          "aria-hidden": false,
          focusable: true
        },
        "aria-atomic": true,
        "aria-live": "polite",
        testId: errorTextId
      }
    ),
    /* @__PURE__ */ jsx(
      Box,
      {
        component: "span",
        sx: visuallyHidden,
        "aria-atomic": "true",
        "aria-live": "polite",
        children: liveRegionText
      }
    )
  ] });
});
QuantitySelector.displayName = COMPONENT_NAME;
export {
  QuantitySelector
};
